import { Content, Horizontal, Stretch, StretchSpacer, Vertical } from "gls/lib";
import { style, stylesheet } from "typestyle";

import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { percent } from "csx";
import { Colours, Sizes, fontFamily } from "../../styles";
import { Button } from "../Button";
import { FormController, FormInputState, FormState } from "./types";

export interface FormProps {
  title?: string;
  onSubmit?: (state: FormState) => void;
  onChange?: (state: FormState) => void;
  submitButtonText?: string;
  submitButtonIsDisabled?: boolean;
  clearButtonText?: string;
  initialState?: FormState;
  spacing?: number | string;
  stretch?: boolean;
  submitButtonIsLoading?: boolean;
}

export const FormContext = createContext<FormController>({} as any);

export const useFormContext = () => {
  return useContext(FormContext);
};

export const Form: FCWithChildren<FormProps> = ({
  initialState,
  title,
  children,
  onSubmit,
  onChange,
  submitButtonText,
  submitButtonIsDisabled,
  submitButtonIsLoading,
  clearButtonText,
  spacing,
  stretch,
}): ReactElement => {
  const formIsInitialized = useRef<boolean>(false);
  const [formState, setFormState] = useState<FormState>(initialState || {});

  const isValid = useMemo(() => {
    if (formIsInitialized.current) {
      return Object.values(formState).every(
        (input) => input.isValid && !(input.isRequired && input.value == null),
      );
    }
  }, [formState, formIsInitialized]);

  useEffect(() => {
    const someFieldIsDirty = Object.values(formState).some(
      (input) => input.isDirty,
    );

    if (someFieldIsDirty) {
      onChange?.(formState);
    }
  }, [formState]);

  useEffect(() => {
    setFormState({
      ...formState,
    });

    formIsInitialized.current = true;
  }, []);

  const clearFormState = () => {
    setFormState({});
  };

  return (
    <FormContext.Provider
      value={[
        formState,
        useCallback(
          (name: string, state: Partial<FormInputState> | undefined) => {
            if (!formIsInitialized.current) {
              formState[name] = {
                ...formState[name],
                ...state,
              };
            } else if (state) {
              setFormState((formState) => ({
                ...formState,
                [name]: {
                  ...formState[name],
                  ...state,
                },
              }));
            } else {
              setFormState((formState) => {
                delete formState[name];
                return { ...formState };
              });
            }
          },
          [formState, setFormState],
        ),
        useCallback(() => !!isValid, [isValid]),
      ]}
    >
      <form
        className={style({
          width: percent(100),
          height: stretch ? percent(100) : "auto",
          boxSizing: "border-box",
        })}
        onSubmit={(event) => {
          const sanitizedObj = JSON.parse(JSON.stringify(formState)); // Removes functions, Symbols, and non-serializable data

          event.preventDefault();
          isValid &&
            !submitButtonIsDisabled &&
            onSubmit?.(structuredClone(sanitizedObj));
        }}
      >
        <Vertical spacing={spacing} height={stretch ? percent(100) : "auto"}>
          {title && <Stretch className={classNames.title}>{title}</Stretch>}

          {children}

          {(clearButtonText || submitButtonText) && (
            <>
              {stretch && <StretchSpacer />}
              <Horizontal horizontalAlign="right">
                {clearButtonText && (
                  <Content
                    horizontalAlign="right"
                    data-testid="form-clear-button"
                  >
                    <Button
                      content={clearButtonText}
                      onClick={() => {
                        clearFormState();
                      }}
                    />
                  </Content>
                )}
                {submitButtonText && (
                  <Content
                    horizontalAlign="right"
                    data-testid="form-submit-button"
                  >
                    <Button
                      loading={submitButtonIsLoading}
                      disabled={!isValid || submitButtonIsDisabled}
                      content={submitButtonText}
                      appearance="blue"
                      type="submit"
                    />
                  </Content>
                )}
              </Horizontal>
            </>
          )}
        </Vertical>
      </form>
    </FormContext.Provider>
  );
};

const classNames = stylesheet({
  title: {
    fontSize: Sizes.fontSize3,
    color: Colours.textLight,
    fontFamily: fontFamily,
  },
});
